import {FC, useState, useEffect, useCallback, useMemo} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {
  Button,
  Col,
  Row,
  Spinner,
  Table,
  Badge,
  Form,
  Modal,
} from 'react-bootstrap'
import Pagination from 'react-bootstrap/Pagination'
import Select from 'react-select'
import {panaAdmin} from '../../../../../services/pana'
import {toast} from 'react-toastify'
import usePagination from '../../../../../utils/hooks/usePagination'
import useDebounce from '../../../../../utils/hooks/useDebounce'
import type {
  LaporanKegiatan,
  Locus,
  PanaBatch,
  LaporanKegiatanIndexParams,
} from '../../../../../types/pana'
import './style.css'

// ─── Helpers ────────────────────────────────────────────────

type LocusOption = {value: number; label: string}

const getCreatedByName = (createdBy: LaporanKegiatan['created_by']): string => {
  if (typeof createdBy !== 'object' || createdBy === null) return 'N/A'
  if ('fullname' in createdBy && createdBy.fullname) return createdBy.fullname
  if ('users_firstname' in createdBy) {
    const first = createdBy.users_firstname || ''
    const last = ('users_lastname' in createdBy && createdBy.users_lastname) || ''
    return `${first} ${last}`.trim() || 'N/A'
  }
  return 'N/A'
}

const getCreatedByEmail = (createdBy: LaporanKegiatan['created_by']): string => {
  if (typeof createdBy !== 'object' || createdBy === null) return ''
  return ('email' in createdBy && createdBy.email) || ''
}

// ─── Component ──────────────────────────────────────────────

const LaporanKegiatanAdmin: FC = () => {
  const [data, setData] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [exportLoading, setExportLoading] = useState<boolean>(false)
  const [selectedLaporan, setSelectedLaporan] = useState<LaporanKegiatan | null>(null)
  const [showApprovalModal, setShowApprovalModal] = useState<boolean>(false)
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [approvalStatus, setApprovalStatus] = useState<0 | 1 | 2>(0)
  const [approvalNote, setApprovalNote] = useState<string>('')

  // Locus & batch dropdown options
  const [locusOptions, setLocusOptions] = useState<LocusOption[]>([])
  const [locusLoading, setLocusLoading] = useState<boolean>(false)
  const [selectedLocus, setSelectedLocus] = useState<LocusOption[]>([])
  const [batchOptions, setBatchOptions] = useState<PanaBatch[]>([])
  const [batchLoading, setBatchLoading] = useState<boolean>(false)
  const [selectedBatch, setSelectedBatch] = useState<string | number>('')

  // Debounced search
  const [searchInput, setSearchInput] = useState<string>('')

  const [pagination, setPagination] = useState({per_page: 10, page: 1})

  const [query, setQuery] = useState({
    search: '',
    sort_field: 'created_at',
    sort_order: 'desc' as 'asc' | 'desc',
    minggu_ke: '' as string | number,
    status: '' as string | number,
  })

  // ─── Pagination hook ──────────────────────────────────────
  const totalPages = useMemo(() => {
    if (!data?.total || !data?.per_page) return 0
    return Math.ceil(data.total / data.per_page)
  }, [data?.total, data?.per_page])

  const paginator = usePagination({
    totalCount: data?.total || 0,
    pageSize: pagination.per_page,
    currentPage: pagination.page,
    siblingCount: 1,
  })

  // ─── Debounce search ──────────────────────────────────────
  useDebounce(
    () => {
      if (searchInput !== query.search) {
        setQuery((prev) => ({...prev, search: searchInput}))
        setPagination((prev) => ({...prev, page: 1}))
      }
    },
    500,
    [searchInput]
  )

  // ─── Fetch filter options on mount ────────────────────────
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setLocusLoading(true)
      setBatchLoading(true)
      try {
        const [locusResponse, batchResponse]: any[] = await Promise.all([
          panaAdmin.getLocus(),
          panaAdmin.getBatch(),
        ])

        const locusItems: Locus[] = locusResponse?.data?.data || locusResponse?.data || []
        setLocusOptions(locusItems.map((item) => ({value: item.id, label: item.locus})))

        const batchItems: PanaBatch[] =
          batchResponse?.data?.data || batchResponse?.data || []
        setBatchOptions(batchItems)
      } catch {
        // Non-critical; fail silently
      } finally {
        setLocusLoading(false)
        setBatchLoading(false)
      }
    }
    fetchFilterOptions()
  }, [])

  const buildIndexFilterParams = useCallback((): LaporanKegiatanIndexParams => {
    const params: LaporanKegiatanIndexParams = {
      search: query.search || undefined,
      sort_field: query.sort_field,
      sort_order: query.sort_order,
      minggu_ke: query.minggu_ke || undefined,
      id_batch: selectedBatch !== '' ? Number(selectedBatch) : undefined,
    }

    if (query.status !== '') {
      params.status = Number(query.status)
    }

    if (selectedLocus.length > 0) {
      params.id_locus = selectedLocus.map((l) => l.value)
    }

    return params
  }, [query, selectedLocus, selectedBatch])

  // ─── Fetch data ────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        ...buildIndexFilterParams(),
        page: pagination.page,
        per_page: pagination.per_page,
      }

      const response = await panaAdmin.getAllLaporanKegiatan(params)
      const responseData = (response as any).data

      setData({
        data: responseData?.data || [],
        total: responseData?.total || 0,
        current_page: responseData?.current_page || pagination.page,
        per_page: responseData?.per_page || pagination.per_page,
      })
    } catch (error: any) {
      console.error('Error fetching laporan:', error)
      toast.error('Gagal memuat data laporan kegiatan')
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.per_page, buildIndexFilterParams])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // ─── Filter helpers ────────────────────────────────────────
  const handleSort = (field: string) => {
    setQuery((prev) => ({
      ...prev,
      sort_field: field,
      sort_order: prev.sort_field === field && prev.sort_order === 'asc' ? 'desc' : 'asc',
    }))
    setPagination((prev) => ({...prev, page: 1}))
  }

  const getSortIcon = (field: string) => {
    if (query.sort_field !== field) {
      return (
        <span className='sort-icon'>
          <i className='bi bi-arrow-down-up text-muted'></i>
        </span>
      )
    }
    return (
      <span className='sort-icon'>
        <i
          className={`bi ${query.sort_order === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} text-primary`}
        ></i>
      </span>
    )
  }

  const handleLocusChange = (selected: readonly LocusOption[]) => {
    setSelectedLocus([...selected])
    setPagination((prev) => ({...prev, page: 1}))
  }

  const handleResetFilters = () => {
    setSearchInput('')
    setSelectedLocus([])
    setSelectedBatch('')
    setQuery({
      search: '',
      sort_field: 'created_at',
      sort_order: 'desc',
      minggu_ke: '',
      status: '',
    })
    setPagination({per_page: 10, page: 1})
  }

  const hasActiveFilters =
    searchInput !== '' ||
    selectedLocus.length > 0 ||
    selectedBatch !== '' ||
    query.minggu_ke !== '' ||
    query.status !== '' ||
    query.sort_field !== 'created_at' ||
    query.sort_order !== 'desc'

  // ─── Approval / Delete ─────────────────────────────────────
  const handleApproval = async () => {
    if (!selectedLaporan) return
    setLoading(true)
    try {
      await panaAdmin.approvalLaporan({
        pana_laporan_kegiatan_id: selectedLaporan.id,
        status: approvalStatus,
        catatan: approvalNote,
      })
      toast.success(
        approvalStatus === 1
          ? 'Laporan berhasil disetujui'
          : approvalStatus === 2
          ? 'Laporan ditolak'
          : 'Status laporan diperbarui'
      )
      setShowApprovalModal(false)
      setSelectedLaporan(null)
      setApprovalNote('')
      fetchData()
    } catch {
      toast.error('Gagal memproses approval')
    } finally {
      setLoading(false)
    }
  }

  const openApprovalModal = (laporan: LaporanKegiatan, status: 0 | 1 | 2) => {
    setSelectedLaporan(laporan)
    setApprovalStatus(status)
    setShowApprovalModal(true)
  }

  const openDetailModal = (laporan: LaporanKegiatan) => {
    setSelectedLaporan(laporan)
    setShowDetailModal(true)
  }

  const openDeleteModal = (laporan: LaporanKegiatan) => {
    setSelectedLaporan(laporan)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedLaporan) return
    setLoading(true)
    try {
      await panaAdmin.deleteLaporanKegiatan(selectedLaporan.id)
      toast.success('Laporan berhasil dihapus')
      setShowDeleteModal(false)
      setSelectedLaporan(null)
      fetchData()
    } catch {
      toast.error('Gagal menghapus laporan')
    } finally {
      setLoading(false)
    }
  }

  const downloadFile = (fileUrl: string, fileName?: string) => {
    const link = document.createElement('a')
    link.href = fileUrl
    link.target = '_blank'
    if (fileName) link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadBase64File = (base64DataUrl: string, fileName: string) => {
    try {
      // Buat link element
      const link = document.createElement('a')
      link.href = base64DataUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading file:', error)
      toast.error('Gagal mengunduh file')
    }
  }

  const handleExportExcel = async () => {
    setExportLoading(true)
    try {
      const response: any = await panaAdmin.exportLaporanKegiatanExcelNew(
        buildIndexFilterParams()
      )

      const {file, name, row_count} = response?.data ?? {}

      if (file && name) {
        downloadBase64File(file, name)
        toast.success(
          row_count != null
            ? `File Excel berhasil diunduh (${row_count} baris)`
            : 'File Excel berhasil diunduh'
        )
      } else {
        toast.error('Format response tidak sesuai')
      }
    } catch (error: any) {
      console.error('Error exporting Excel:', error)
      toast.error('Gagal mengekspor data ke Excel')
    } finally {
      setExportLoading(false)
    }
  }

  const onChangePerPage = (per_page: number) => {
    setPagination({per_page, page: 1})
  }

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return <Badge bg='warning'>Pending</Badge>
      case 1:
        return <Badge bg='success'>Approved</Badge>
      case 2:
        return <Badge bg='danger'>Rejected</Badge>
      default:
        return <Badge bg='secondary'>Unknown</Badge>
    }
  }

  // ─── Render ────────────────────────────────────────────────
  const items: LaporanKegiatan[] = data?.data || []

  return (
    <>
      <PageTitle breadcrumbs={[]}>Laporan Kegiatan - Admin</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              Monitoring Laporan Kegiatan Semua User
            </span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Total: {data?.total || 0} laporan
            </span>
          </h3>
          <div className='card-toolbar'>
            <Button
              variant='success'
              size='sm'
              onClick={handleExportExcel}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Mengunduh...
                </>
              ) : (
                <>
                  <i className='bi bi-file-earmark-excel me-2'></i>
                  Export Excel
                </>
              )}
            </Button>
          </div>
        </div>

        {/* ─── Filters ──────────────────────────────────────── */}
        <div className='card-body py-3 filter-section'>
          {/* Row 1: Search */}
          <Row className='mb-3'>
            <Col xs={12}>
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <div className='search-wrapper'>
                  <Form.Control
                    type='text'
                    placeholder='Cari judul, nama pembuat, atau nama ketua tim...'
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <button
                      type='button'
                      className='search-clear-btn'
                      onClick={() => setSearchInput('')}
                    >
                      <i className='bi bi-x-circle-fill'></i>
                    </button>
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* Row 2: Filters */}
          <Row className='mb-3 g-3'>
            <Col xs={6} md={4} lg={2}>
              <Form.Group>
                <Form.Label>Minggu Ke</Form.Label>
                <Form.Select
                  value={query.minggu_ke}
                  onChange={(e) => {
                    setQuery((prev) => ({...prev, minggu_ke: e.target.value}))
                    setPagination((prev) => ({...prev, page: 1}))
                  }}
                >
                  <option value=''>Semua</option>
                  {Array.from({length: 12}, (_, i) => i + 1).map((minggu) => (
                    <option key={minggu} value={minggu}>
                      Minggu {minggu}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={4} lg={2}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={query.status}
                  onChange={(e) => {
                    setQuery((prev) => ({...prev, status: e.target.value}))
                    setPagination((prev) => ({...prev, page: 1}))
                  }}
                >
                  <option value=''>Semua</option>
                  <option value='0'>Pending</option>
                  <option value='1'>Approved</option>
                  <option value='2'>Rejected</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={4} lg={2}>
              <Form.Group>
                <Form.Label>Batch</Form.Label>
                <Form.Select
                  value={selectedBatch}
                  disabled={batchLoading}
                  onChange={(e) => {
                    setSelectedBatch(e.target.value)
                    setPagination((prev) => ({...prev, page: 1}))
                  }}
                >
                  <option value=''>Semua</option>
                  {batchOptions.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {batch.nama_batch}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={4} lg={3}>
              <Form.Group>
                <Form.Label>Locus</Form.Label>
                <Select
                  isMulti
                  options={locusOptions}
                  value={selectedLocus}
                  onChange={handleLocusChange}
                  isLoading={locusLoading}
                  placeholder='Pilih locus...'
                  noOptionsMessage={() => 'Tidak ada data locus'}
                  classNamePrefix='react-select'
                  className='locus-select'
                  isClearable
                />
              </Form.Group>
            </Col>
            <Col xs={6} md={4} lg={2}>
              <Form.Group>
                <Form.Label>Per Page</Form.Label>
                <Form.Select
                  value={pagination.per_page}
                  onChange={(e) => onChangePerPage(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={4} lg={2} className='d-flex align-items-end'>
              {hasActiveFilters && (
                <Button
                  variant='light'
                  size='sm'
                  className='btn-reset-filter'
                  onClick={handleResetFilters}
                >
                  <i className='bi bi-arrow-clockwise'></i> Reset Filter
                </Button>
              )}
            </Col>
          </Row>

          {/* ─── Desktop Table ───────────────────────────────── */}
          <div className='table-responsive laporan-table-desktop'>
            <Table hover className='table-row-bordered table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bold text-muted bg-light'>
                  <th
                    className='min-w-50px ps-4 sortable-header'
                    onClick={() => handleSort('minggu_ke')}
                  >
                    Minggu {getSortIcon('minggu_ke')}
                  </th>
                  <th
                    className='min-w-150px sortable-header'
                    onClick={() => handleSort('nama_peserta')}
                  >
                    User {getSortIcon('nama_peserta')}
                  </th>
                  <th
                    className='min-w-200px sortable-header'
                    onClick={() => handleSort('judul')}
                  >
                    Judul {getSortIcon('judul')}
                  </th>
                  <th
                    className='min-w-120px sortable-header'
                    onClick={() => handleSort('status')}
                  >
                    Status {getSortIcon('status')}
                  </th>
                  <th
                    className='min-w-100px sortable-header'
                    onClick={() => handleSort('created_at')}
                  >
                    Tanggal {getSortIcon('created_at')}
                  </th>
                  <th className='min-w-100px text-end pe-4'>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className='text-center py-5'>
                      <Spinner animation='border' />
                    </td>
                  </tr>
                ) : items.length > 0 ? (
                  items.map((laporan) => (
                    <tr key={laporan.id}>
                      <td className='ps-4'>
                        <span className='badge badge-light-primary'>
                          Minggu {laporan.minggu_ke}
                        </span>
                      </td>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='fw-bold'>
                            {getCreatedByName(laporan.created_by)}
                          </span>
                          <span className='text-muted fs-7'>
                            {getCreatedByEmail(laporan.created_by)}
                          </span>
                          {/* {laporan.pendaftaran_pana?.nomor_pendaftaran && (
                            <span className='text-muted fs-8'>
                              {laporan.pendaftaran_pana.nomor_pendaftaran}
                            </span>
                          )} */}
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='fw-semibold'>{laporan.judul}</span>
                          {laporan.deskripsi && (
                            <span className='text-muted fs-7'>
                              {laporan.deskripsi.substring(0, 60)}
                              {laporan.deskripsi.length > 60 ? '...' : ''}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{getStatusBadge(laporan.status)}</td>
                      <td>
                        <span className='text-muted fs-7'>
                          {new Date(laporan.created_at).toLocaleDateString('id-ID')}
                        </span>
                      </td>
                      <td className='text-end pe-4'>
                        <Button
                          size='sm'
                          variant='info'
                          className='me-2'
                          onClick={() => openDetailModal(laporan)}
                        >
                          <i className='bi bi-eye'></i>
                        </Button>
                        <Button
                          size='sm'
                          variant='success'
                          className='me-2'
                          disabled={laporan.status === 1}
                          onClick={() => openApprovalModal(laporan, 1)}
                        >
                          <i className='bi bi-check-circle'></i>
                        </Button>
                        <Button
                          size='sm'
                          variant='danger'
                          className='me-2'
                          disabled={laporan.status === 2}
                          onClick={() => openApprovalModal(laporan, 2)}
                        >
                          <i className='bi bi-x-circle'></i>
                        </Button>
                        <Button
                          size='sm'
                          variant='outline-danger'
                          onClick={() => openDeleteModal(laporan)}
                        >
                          <i className='bi bi-trash'></i>
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className='text-center py-10'>
                      <div className='text-gray-600'>
                        <i className='bi bi-inbox fs-2x mb-3 d-block'></i>
                        Tidak ada data
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* ─── Mobile Card View ────────────────────────────── */}
          <div className='laporan-card-mobile'>
            {loading ? (
              <div className='text-center py-5'>
                <Spinner animation='border' />
              </div>
            ) : items.length > 0 ? (
              items.map((laporan) => (
                <div key={laporan.id} className='laporan-card-item'>
                  <div className='card-header-row'>
                    <div className='card-title-section'>
                      <div className='judul'>{laporan.judul}</div>
                      {laporan.deskripsi && (
                        <div className='deskripsi'>
                          {laporan.deskripsi.substring(0, 80)}
                          {laporan.deskripsi.length > 80 ? '...' : ''}
                        </div>
                      )}
                    </div>
                    <div className='ms-2'>{getStatusBadge(laporan.status)}</div>
                  </div>

                  <div className='card-meta'>
                    <span>
                      <i className='bi bi-calendar3 me-1'></i>
                      Minggu {laporan.minggu_ke}
                    </span>
                    <span>
                      <i className='bi bi-person me-1'></i>
                      {getCreatedByName(laporan.created_by)}
                    </span>
                    <span>
                      <i className='bi bi-clock me-1'></i>
                      {new Date(laporan.created_at).toLocaleDateString('id-ID')}
                    </span>
                    {laporan.pendaftaran_pana?.nomor_pendaftaran && (
                      <span>
                        <i className='bi bi-hash me-1'></i>
                        {laporan.pendaftaran_pana.nomor_pendaftaran}
                      </span>
                    )}
                  </div>

                  <div className='card-actions'>
                    <Button size='sm' variant='info' onClick={() => openDetailModal(laporan)}>
                      <i className='bi bi-eye me-1'></i>Detail
                    </Button>
                    <Button
                      size='sm'
                      variant='success'
                      disabled={laporan.status === 1}
                      onClick={() => openApprovalModal(laporan, 1)}
                    >
                      <i className='bi bi-check-circle me-1'></i>Setujui
                    </Button>
                    <Button
                      size='sm'
                      variant='danger'
                      disabled={laporan.status === 2}
                      onClick={() => openApprovalModal(laporan, 2)}
                    >
                      <i className='bi bi-x-circle me-1'></i>Tolak
                    </Button>
                    <Button
                      size='sm'
                      variant='outline-danger'
                      onClick={() => openDeleteModal(laporan)}
                    >
                      <i className='bi bi-trash'></i>
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className='text-center py-10 text-gray-600'>
                <i className='bi bi-inbox fs-2x mb-3 d-block'></i>
                Tidak ada data
              </div>
            )}
          </div>

          {/* ─── Pagination ──────────────────────────────────── */}
          {!loading && items.length > 0 && (
            <div className='pagination-footer'>
              <div className='text-muted small'>
                Menampilkan{' '}
                {(data.current_page - 1) * data.per_page + 1} -{' '}
                {Math.min(data.current_page * data.per_page, data.total)} dari{' '}
                {data.total} data
              </div>
              {paginator && (
                <Pagination size='sm'>
                  <Pagination.First
                    disabled={pagination.page === 1}
                    onClick={() => setPagination((prev) => ({...prev, page: 1}))}
                  />
                  <Pagination.Prev
                    disabled={pagination.page === 1}
                    onClick={() =>
                      setPagination((prev) => ({...prev, page: prev.page - 1}))
                    }
                  />
                  {paginator.map((page: any, index: number) => {
                    if (page === '...') {
                      return <Pagination.Ellipsis key={`dots-${index}`} disabled />
                    }
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === pagination.page}
                        onClick={() => setPagination((prev) => ({...prev, page}))}
                      >
                        {page}
                      </Pagination.Item>
                    )
                  })}
                  <Pagination.Next
                    disabled={pagination.page >= totalPages}
                    onClick={() =>
                      setPagination((prev) => ({...prev, page: prev.page + 1}))
                    }
                  />
                  <Pagination.Last
                    disabled={pagination.page >= totalPages}
                    onClick={() =>
                      setPagination((prev) => ({...prev, page: totalPages}))
                    }
                  />
                </Pagination>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Approval Modal ──────────────────────────────────── */}
      <Modal show={showApprovalModal} onHide={() => setShowApprovalModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {approvalStatus === 1 ? 'Setujui Laporan' : 'Tolak Laporan'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLaporan && (
            <>
              <div className='mb-3'>
                <strong>Judul:</strong> {selectedLaporan.judul}
              </div>
              <div className='mb-3'>
                <strong>Minggu:</strong> {selectedLaporan.minggu_ke}
              </div>
              <Form.Group>
                <Form.Label>
                  Catatan {approvalStatus === 2 ? '(Wajib)' : '(Opsional)'}
                </Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  placeholder='Tambahkan catatan...'
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowApprovalModal(false)}>
            Batal
          </Button>
          <Button
            variant={approvalStatus === 1 ? 'success' : 'danger'}
            onClick={handleApproval}
            disabled={loading || (approvalStatus === 2 && !approvalNote)}
          >
            {loading ? <Spinner animation='border' size='sm' /> : 'Konfirmasi'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Detail Modal ────────────────────────────────────── */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size='lg'
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Detail Laporan Kegiatan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLaporan ? (
            <div>
              <Row className='mb-3'>
                <Col md={6}>
                  <div className='mb-3'>
                    <small className='text-muted'>User</small>
                    <div className='fw-bold'>
                      {getCreatedByName(selectedLaporan.created_by)}
                    </div>
                    <div className='text-muted fs-7'>
                      {getCreatedByEmail(selectedLaporan.created_by)}
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className='mb-3'>
                    <small className='text-muted'>Minggu Ke</small>
                    <div>
                      <span className='badge badge-light-primary fs-6'>
                        Minggu {selectedLaporan.minggu_ke}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className='mb-3'>
                    <small className='text-muted'>Status</small>
                    <div>{getStatusBadge(selectedLaporan.status)}</div>
                  </div>
                </Col>
              </Row>

              <div className='mb-3'>
                <small className='text-muted'>Judul</small>
                <div className='fw-bold fs-5' style={{wordBreak: 'break-word'}}>
                  {selectedLaporan.judul}
                </div>
              </div>

              <div className='mb-3'>
                <small className='text-muted'>Deskripsi</small>
                <div
                  className='p-3 bg-light rounded'
                  style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {selectedLaporan.deskripsi || '-'}
                </div>
              </div>

              {selectedLaporan.catatan && (
                <div className='mb-3'>
                  <small className='text-muted'>Catatan Admin</small>
                  <div
                    className='p-3 bg-light-warning rounded'
                    style={{
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <i className='bi bi-chat-left-text text-warning me-2'></i>
                    {selectedLaporan.catatan}
                  </div>
                </div>
              )}

              {selectedLaporan.link && (
                <div className='mb-3'>
                  <small className='text-muted'>Link External</small>
                  <div style={{wordBreak: 'break-all', overflowWrap: 'break-word'}}>
                    <a
                      href={selectedLaporan.link}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {selectedLaporan.link}
                    </a>
                  </div>
                </div>
              )}

              <div className='mb-3'>
                <small className='text-muted'>Tanggal Dibuat</small>
                <div>
                  {new Date(selectedLaporan.created_at).toLocaleString('id-ID')}
                </div>
              </div>

              {selectedLaporan.files && selectedLaporan.files.length > 0 && (
                <div className='mb-3'>
                  <small className='text-muted'>
                    File Attachment ({selectedLaporan.files.length})
                  </small>
                  <div className='mt-2'>
                    {selectedLaporan.files.map((file: any, index: number) => (
                      <div
                        key={file.id || index}
                        className='d-flex align-items-center justify-content-between p-2 border rounded mb-2'
                      >
                        <div className='d-flex align-items-center'>
                          <i className='bi bi-file-earmark fs-3 me-3 text-primary'></i>
                          <div style={{maxWidth: '300px'}}>
                            <div
                              className='fw-semibold'
                              style={{wordBreak: 'break-word'}}
                            >
                              {file.path?.split('/').pop() || `File ${index + 1}`}
                            </div>
                            <small className='text-muted'>
                              {new Date(file.created_at).toLocaleDateString('id-ID')}
                            </small>
                          </div>
                        </div>
                        <Button
                          size='sm'
                          variant='outline-primary'
                          onClick={() =>
                            downloadFile(file.path, file.path?.split('/').pop())
                          }
                        >
                          <i className='bi bi-download'></i> Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLaporan.pendaftaran_pana && (
                <div className='mt-4 p-3 bg-light-info rounded'>
                  <small className='text-muted'>Info Pendaftaran</small>
                  <div className='mt-2' style={{wordBreak: 'break-word'}}>
                    <strong>Judul Proposal:</strong>{' '}
                    {selectedLaporan.pendaftaran_pana.judul_proposal}
                  </div>
                  {selectedLaporan.pendaftaran_pana.nomor_pendaftaran && (
                    <div style={{wordBreak: 'break-word'}}>
                      <strong>No. Registrasi:</strong>{' '}
                      {selectedLaporan.pendaftaran_pana.nomor_pendaftaran}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className='text-center py-5'>
              <Spinner animation='border' />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowDetailModal(false)}>
            Tutup
          </Button>
          {selectedLaporan && selectedLaporan.status === 0 && (
            <>
              <Button
                variant='success'
                onClick={() => {
                  setShowDetailModal(false)
                  openApprovalModal(selectedLaporan, 1)
                }}
              >
                <i className='bi bi-check-circle'></i> Setujui
              </Button>
              <Button
                variant='danger'
                onClick={() => {
                  setShowDetailModal(false)
                  openApprovalModal(selectedLaporan, 2)
                }}
              >
                <i className='bi bi-x-circle'></i> Tolak
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* ─── Delete Confirmation Modal ────────────────────────── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Konfirmasi Hapus</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLaporan && (
            <>
              <div className='alert alert-warning'>
                <i className='bi bi-exclamation-triangle'></i> Apakah Anda yakin ingin
                menghapus laporan ini?
              </div>
              <div className='mb-2'>
                <strong>Judul:</strong> {selectedLaporan.judul}
              </div>
              <div className='mb-2'>
                <strong>Minggu:</strong> {selectedLaporan.minggu_ke}
              </div>
              <div className='mb-2'>
                <strong>User:</strong>{' '}
                {getCreatedByName(selectedLaporan.created_by)}
              </div>
              <small className='text-danger'>
                Data yang dihapus tidak dapat dikembalikan.
              </small>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowDeleteModal(false)}>
            Batal
          </Button>
          <Button variant='danger' onClick={handleDelete} disabled={loading}>
            {loading ? <Spinner animation='border' size='sm' /> : 'Hapus'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default LaporanKegiatanAdmin
